"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import cn from "classnames"
import { gsap } from "gsap"

import { useScroll } from "~/components/foundations/ScrollProvider"
import { Icon } from "~/components/primitives/icon"
import { prefersReducedMotion } from "~/functions/prefersReducedMotion"
import type { CtaLink } from "~/types/cms"

import { Burger } from "./Burger"
import s from "./Header.module.scss"
import { MainNav } from "./MainNav"

const SCROLL_THRESHOLD = 200

type HeaderClientProps = {
  links: CtaLink[]
}

/**
 * HeaderClient component sets up and manages the behavior of the page header, including scroll effects and navigation.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.links - The list of navigation links to be rendered in the main navigation.
 * @return {JSX.Element} The rendered header component.
 */
export const HeaderClient = ({ links }: HeaderClientProps) => {
  const headerRef = useRef<HTMLElement>(null)
  const { lenis } = useScroll()
  const routeParam = usePathname()

  const [isOpen, setIsOpen] = useState(false)

  const [isFullSize, setIsFullSize] = useState(false)

  const [isFooterVisible, setIsFooterVisible] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsFullSize(window.innerWidth >= 1024)
    }

    handleResize()

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (!lenis || !headerRef.current) return

    const logo = headerRef.current.querySelector<SVGSVGElement>(".z-logo")
    if (!logo) return

    const handleScroll = ({ scroll }: { scroll: number }) => {
      const scrolled = scroll > SCROLL_THRESHOLD

      if (!headerRef.current) return

      headerRef.current.dataset.scrolled = scrolled ? "true" : "false"

      gsap.to(logo, {
        opacity: scrolled ? 0 : 1,
        duration: prefersReducedMotion() ? 0 : 0.4,
        ease: "power2.out",
        overwrite: true,
      })
    }

    lenis.on("scroll", handleScroll)

    return () => {
      lenis.off("scroll", handleScroll)
    }
  }, [lenis])

  // Fade the header out once the footer scrolls into view (and back in when
  // it scrolls out), so the fixed nav doesn't sit on top of footer content.
  // Re-queries the footer on every tick instead of caching the node in an
  // IntersectionObserver target: if the footer's DOM node is ever replaced
  // (observed in production builds, not dev - likely a hydration-driven
  // remount) a cached target goes stale and silently stops firing forever.
  useEffect(() => {
    if (!lenis) return

    const handleScroll = () => {
      // Query within this lenis instance's own wrapper, not the whole document:
      // page transitions briefly mount two SmoothScrollWrapper/Footer trees at
      // once (see SharedRouterTransition), and a document-wide query can latch
      // onto the outgoing footer just before it unmounts, permanently orphaning
      // the observer since this effect only re-runs when `lenis` changes.
      const footer = lenis.rootElement.querySelector('[data-element="footer"]')
      if (!footer) return

      // Counts as "visible" once the footer has scrolled up into the top 55% of the
      // viewport, so the header stays up longer on the way down and reappears sooner
      // on the way back up (same crossing point both ways, viewport-relative so this
      // stays consistent even when the footer is taller than the viewport).
      const footerTop = footer.getBoundingClientRect().top
      setIsFooterVisible(footerTop < window.innerHeight * 0.55)
    }

    handleScroll()
    lenis.on("scroll", handleScroll)

    return () => {
      lenis.off("scroll", handleScroll)
    }
  }, [lenis])

  useEffect(() => {
    if (isFullSize) setIsOpen(false)
  }, [isFullSize])

  useEffect(() => {
    const header = headerRef.current
    if (!header) return

    const handleClick = (event: MouseEvent) => {
      if ((event.target as HTMLElement).closest("a")) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false)
      }
    }

    header.addEventListener("click", handleClick)
    window.addEventListener("keydown", handleKeyDown)
    return () => header.removeEventListener("click", handleClick)
  }, [setIsOpen, isOpen])

  return (
    <header
      ref={headerRef}
      inert={isFooterVisible}
      className={cn(s.header)}
      data-element="header"
      data-fullsize={isFullSize}
      data-open={isOpen}
      data-footer-visible={isFooterVisible}
    >
      <div className={s.mobileMenu}>
        <Link
          href="/"
          className={s.logoLink}
          aria-label="Link to homepage"
          aria-current={routeParam === "/" ? "page" : undefined}
        >
          <Icon id="logo" className={"z-logo"} title="Meraki logo" />
          <Icon
            id="logo-effect"
            className={"z-logo-effect"}
            aria-hidden="true"
          />
          <svg
            className={s.logoMobile}
            viewBox="0 0 45 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Meraki logo</title>
            <path
              d="M6.49051 27.97L4.49051 26.554C3.71022 26.005 2.96075 25.4135 2.24551 24.782C1.79592 24.3867 1.38291 23.9516 1.01151 23.482C0.645277 23.0459 0.36891 22.5417 0.198319 21.9983C0.0277272 21.4549 -0.0337204 20.8832 0.0175124 20.316C0.0990673 19.5848 0.412002 18.8987 0.910718 18.3577C1.40943 17.8168 2.06787 17.4492 2.79008 17.3085C3.51229 17.1679 4.26054 17.2616 4.9258 17.5759C5.59105 17.8903 6.13855 18.4088 6.48851 19.056C6.8386 18.4093 7.386 17.8911 8.051 17.577C8.71601 17.263 9.4639 17.1694 10.1858 17.3098C10.9077 17.4503 11.5659 17.8175 12.0646 18.3581C12.5633 18.8986 12.8765 19.5842 12.9585 20.315C13.0098 20.8822 12.9484 21.4538 12.778 21.9971C12.6076 22.5405 12.3315 23.0448 11.9655 23.481C11.5935 23.9504 11.1802 24.3855 10.7305 24.781C10.0152 25.4124 9.26578 26.004 8.48551 26.553L6.49051 27.97Z"
              fill="#00FFE2"
            />
            <path
              d="M29.786 5.11852e-06H23.9152V4.01233C21.1234 1.42782 17.4828 -0.00312321 13.7073 5.11852e-06H2.99512V12.3284H11.4768V8.63991H13.7093C15.4877 8.64206 17.1927 9.36148 18.4502 10.6403C19.7077 11.9192 20.4149 13.653 20.4167 15.4615V28H28.9144V15.4625C28.917 13.0963 28.3823 10.7615 27.3518 8.63991H29.789C31.5676 8.64179 33.2729 9.3611 34.5306 10.64C35.7882 11.9189 36.4956 13.6529 36.4975 15.4615V28H44.9951V15.4625C44.9906 11.363 43.3871 7.4327 40.5364 4.53391C37.6857 1.63512 33.8205 0.00457341 29.789 5.11852e-06"
              fill="white"
            />
          </svg>
        </Link>
        {!isFullSize && (
          <Burger
            active={isOpen}
            controls="main-nav"
            className={cn(s.burger)}
            onClick={() => setIsOpen((prev) => !prev)}
          />
        )}
      </div>
      <MainNav
        links={links}
        isOpen={isOpen}
        isFullSize={isFullSize}
        setIsOpen={setIsOpen}
      />
    </header>
  )
}
